import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface ScaleCalibrationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  previewSvg: string;
  onApply: (scaleFactor: number) => void;
}

export const ScaleCalibrationDialog = ({
  open,
  onOpenChange,
  previewSvg,
  onApply
}: ScaleCalibrationDialogProps) => {
  const [knownDistance, setKnownDistance] = useState<string>('');
  const [calculatedScale, setCalculatedScale] = useState<number | null>(null);

  const handleCalculate = () => {
    // In a real implementation, this would:
    // 1. Let user click 2 points on the SVG
    // 2. Calculate pixel distance between points
    // 3. Use known distance to calculate scale factor
    // For now, we'll use a placeholder
    const distance = parseFloat(knownDistance);
    if (!isNaN(distance) && distance > 0) {
      // Placeholder: assume 100 pixels = entered distance
      const scale = distance / 100;
      setCalculatedScale(scale);
    }
  };

  const handleApply = () => {
    if (calculatedScale) {
      onApply(calculatedScale);
      onOpenChange(false);
      setKnownDistance('');
      setCalculatedScale(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Calibrate Scale</DialogTitle>
          <DialogDescription>
            Click two points on the drawing and enter the known distance between them in feet.
          </DialogDescription>
        </DialogHeader>

        <Alert>
          <AlertDescription>
            The DXF file doesn't contain scale information or the detected scale seems incorrect.
            Please calibrate manually by selecting two points with a known distance.
          </AlertDescription>
        </Alert>

        <div className="border rounded-lg overflow-auto bg-muted/20" style={{ maxHeight: '400px' }}>
          <div
            className="flex items-center justify-center p-4"
            dangerouslySetInnerHTML={{ __html: previewSvg }}
          />
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="knownDistance">Known Distance (feet)</Label>
            <Input
              id="knownDistance"
              type="number"
              placeholder="Enter distance in feet"
              value={knownDistance}
              onChange={(e) => setKnownDistance(e.target.value)}
              step="0.1"
            />
            <p className="text-sm text-muted-foreground">
              Click on the drawing to select 2 points, then enter the actual distance between them.
            </p>
          </div>

          <Button onClick={handleCalculate} className="w-full" disabled={!knownDistance}>
            Calculate Scale
          </Button>

          {calculatedScale && (
            <Alert>
              <AlertDescription>
                Calculated scale factor: {calculatedScale.toFixed(4)}
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleApply} disabled={!calculatedScale}>
            Apply Scale & Recalculate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
